import React, { useState, useEffect } from "react";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";
import AsyncSelect from "react-select/async";

const OperationalPopup = ({ closePopup, editData, triggerReload }) => {
  const [formData, setFormData] = useState({
    project_code: "",
    assumptions: "",
    terms: ""
  });
  const [isEditing, setIsEditing] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedAssumption, setSelectedAssumption] = useState(null);
  const [selectedTerms, setSelectedTerms] = useState(null);

  useEffect(() => {
    if (editData?.id) {
      const fetchData = async () => {
        try {
          const response = await axiosInstance.get(
            `/costsolution/operationalassumptions/get/${editData.id}`
          );
          const data = response.data.projectoperationalassumptions;

          setFormData({
            project_code: data.projectCode || "",
            assumptions: data.assumptions?.toString() || "",
            terms: data.terms?.toString() || ""
          });

          if (data.projectCode) {
            setSelectedProject({
              value: data.projectCode,
              label: data.projectName
            });
          }

          // Set selected assumption if needed
          if (data.assumptions) {
            setSelectedAssumption({
              value: data.assumptions,
              label: data.assumptionName || `Assumption ${data.assumptions}`
            });
          }

          // Set selected terms if needed
          if (data.terms) {
            setSelectedTerms({
              value: data.terms,
              label: data.termName || `Terms ${data.terms}`
            });
          }

          setIsEditing(true);
        } catch (error) {
          toast.error("Failed to fetch operational details");
          console.error("Fetch error:", error);
        }
      };
      fetchData();
    }
  }, [editData?.id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEditing) {
      handleUpdate();
    } else {
      handleSave();
    }
  };

  const handleSave = async () => {
    const postData = {
      project_code: String(formData.project_code),
      assumptions: String(formData.assumptions),
      terms: String(formData.terms)
    };

    try {
      const response = await axiosInstance.post("/costsolution/operationalassumptions", postData);
      if (response.data.success || response.data.id) {
        toast.success("Operational created successfully!");
        triggerReload();
        closePopup();
      } else {
        toast.error("This project already exists!");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.message || "Something went wrong!");
    }
  };

  const handleUpdate = async () => {
    const updateData = {
      project_code: String(formData.project_code),
      assumptions: String(formData.assumptions),
      terms: String(formData.terms)
    };

    try {
      const response = await axiosInstance.put(
        `/costsolution/operationalassumptions/${editData.id}`,
        updateData
      );
      if (response.data.status) {
        toast.success("Operational updated successfully!");
        triggerReload();
        closePopup();
      } else {
        toast.error("This project already exists!");
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.response?.data?.message || "Something went wrong!");
    }
  };


  const handleClear = () => {
    setFormData({
      project_code: "",
      assumptions: "",
      terms: ""
    });
    setSelectedProject(null);
    setSelectedAssumption(null);
    setSelectedTerms(null);
  };

  const handleProjectSelect = (selectedOption) => {
    setSelectedProject(selectedOption);
    setFormData(prev => ({
      ...prev,
      project_code: selectedOption ? selectedOption.value : ''
    }));
  };

  const handleAssumptionSelect = (selectedOption) => {
    setSelectedAssumption(selectedOption);
    setFormData(prev => ({
      ...prev,
      assumptions: selectedOption ? selectedOption.value : ''
    }));
  };

  const handleTermsSelect = (selectedOption) => {
    setSelectedTerms(selectedOption);
    setFormData(prev => ({
      ...prev,
      terms: selectedOption ? selectedOption.value : ''
    }));
  };

  const loadProjectOptions = async (inputValue, callback) => {
    try {
      const res = await axiosInstance.get("/costsolution/projects/autosuggestion", {
        params: { term: inputValue || "" }
      });

      const projects = res.data?.autosuggestion || [];
      const filtered = projects.filter(item =>
        item.label.toUpperCase().includes(inputValue.toUpperCase())
      );

      callback(filtered.map(item => ({
        label: item.label,
        value: item.value
      })));
    } catch (error) {
      console.error("Project suggestion error:", error);
      callback([]);
    }
  };

  const loadOperationalOptions = async (inputValue, callback) => {
    try {
      const res = await axiosInstance.get("/costsolution/assumptions/autosuggestion", {
        params: { term: inputValue || "" }
      });

      const assumptions = res.data?.autosuggestion || [];
      callback(assumptions.map(item => ({
        label: item.label,
        value: item.value
      })));
    } catch (error) {
      console.error("Assumption suggestion error:", error);
      callback([]);
    }
  };

  const loadTermsOptions = async (inputValue, callback) => {
    try {
      const res = await axiosInstance.get("/costsolution/termsandcondition/autosuggestion", {
        params: { term: inputValue || "" }
      });

      const terms = res.data?.autosuggestion || [];
      callback(terms.map(item => ({
        label: item.label,
        value: item.value
      })));
    } catch (error) {
      console.error("Terms suggestion error:", error);
      callback([]);
    }
  };

  return (
    <div className="scope-creation-popup-overlay">
      <div className="scope-creation-popup-container">
        {/* Header */}
        <div className="scope-creation-popup-header">
          <h2>{isEditing ? "Edit Operational" : "Create New Operational"}</h2>
          <button className="close-button" onClick={closePopup}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="scope-creation-popup-body">
            <div className="scope-creation-form-group">
              <label>Project</label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadProjectOptions}
                value={selectedProject}
                onChange={handleProjectSelect}
                placeholder="Search project..."
                isClearable
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '34px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    borderColor: state.isFocused ? '#454CF2' : '#DCDFE4',
                    boxShadow: state.isFocused ? '0 0 0 1px #454CF2' : 'none',
                    '&:hover': { borderColor: '#454CF2' }
                  }),
                  container: base => ({ ...base, width: '100%' }),
                  menu: base => ({
                    ...base,
                    zIndex: 999,
                    fontSize: '13px',
                    marginTop: '4px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }),
                  menuList: base => ({
                    ...base,
                    maxHeight: '180px',
                    overflowY: 'auto',
                    padding: 0
                  }),
                  option: (base, { isFocused }) => ({
                    ...base,
                    backgroundColor: isFocused ? '#f2f2f2' : '#fff',
                    color: '#333',
                    padding: '8px',
                    height: '30px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    '&:active': { backgroundColor: '#f2f2f2' }
                  }),
                  dropdownIndicator: base => ({ ...base, padding: '8px' }),
                  clearIndicator: base => ({ ...base, padding: '8px' })
                }}
              />
            </div>

            <div className="scope-creation-form-group">
              <label className="name">Assumption</label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadOperationalOptions}
                onChange={handleAssumptionSelect}
                value={selectedAssumption}
                placeholder="Search project..."
                isClearable
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '34px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    borderColor: state.isFocused ? '#454CF2' : '#DCDFE4',
                    boxShadow: state.isFocused ? '0 0 0 1px #454CF2' : 'none',
                    '&:hover': { borderColor: '#454CF2' }
                  }),
                  container: base => ({ ...base, width: '100%' }),
                  menu: base => ({
                    ...base,
                    zIndex: 999,
                    fontSize: '13px',
                    marginTop: '4px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }),
                  menuList: base => ({
                    ...base,
                    maxHeight: '180px',
                    overflowY: 'auto',
                    padding: 0
                  }),
                  option: (base, { isFocused }) => ({
                    ...base,
                    backgroundColor: isFocused ? '#f2f2f2' : '#fff',
                    color: '#333',
                    padding: '8px',
                    height: '30px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    '&:active': { backgroundColor: '#f2f2f2' }
                  }),
                  dropdownIndicator: base => ({ ...base, padding: '8px' }),
                  clearIndicator: base => ({ ...base, padding: '8px' })
                }}
              />
            </div>

            <div className="scope-creation-form-group">
              <label className="name">Terms</label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadTermsOptions}
                onChange={handleTermsSelect}
                value={selectedTerms}
                placeholder="Search project..."
                isClearable
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '34px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    borderColor: state.isFocused ? '#454CF2' : '#DCDFE4',
                    boxShadow: state.isFocused ? '0 0 0 1px #454CF2' : 'none',
                    '&:hover': { borderColor: '#454CF2' }
                  }),
                  container: base => ({ ...base, width: '100%' }),
                  menu: base => ({
                    ...base,
                    zIndex: 999,
                    fontSize: '13px',
                    marginTop: '4px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }),
                  menuList: base => ({
                    ...base,
                    maxHeight: '180px',
                    overflowY: 'auto',
                    padding: 0
                  }),
                  option: (base, { isFocused }) => ({
                    ...base,
                    backgroundColor: isFocused ? '#f2f2f2' : '#fff',
                    color: '#333',
                    padding: '8px',
                    height: '30px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    '&:active': { backgroundColor: '#f2f2f2' }
                  }),
                  dropdownIndicator: base => ({ ...base, padding: '8px' }),
                  clearIndicator: base => ({ ...base, padding: '8px' })
                }}
              />
            </div>
          </div>

          <div className="scope-creation-popup-footer">
            <button type="button" className="clear-button" onClick={handleClear}>
              Clear
            </button>
            <button type="submit" className="submit-button">
              {isEditing ? "Update" : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OperationalPopup;
